import React, { useEffect, useMemo, useRef, useState } from 'react'
import L from 'leaflet'
import { Marker, Circle, Tooltip, useMap } from 'react-leaflet'
import icon from 'leaflet/dist/images/marker-icon.png'
import iconShadow from 'leaflet/dist/images/marker-shadow.png'
import { drawLocalSetting } from './leafletDrawTooltipSetting'
import 'leaflet-draw'
import { useTranslation } from 'react-i18next'

const DefaultIcon = L.icon({ iconUrl: icon, shadowUrl: iconShadow, iconSize: [24, 36], iconAnchor: [12, 36] })

const inputStyle = (id, placeholder, width, min, max, type) => {
  const input = L.DomUtil.create('input')
  input.id = id
  input.style.width = width
  input.style.padding = '1px 3px'
  input.style.backgroundColor = 'white'
  input.style.outline = 'none'
  input.style.border = 'none'
  input.placeholder = placeholder
  input.min = min
  input.max = max
  input.type = type
  return input
}

const inputControl = (labelName, id, placeholder, width, min, max, type, listener = () => {}) => {
  const div = L.DomUtil.create('div')
  div.style.backgroundColor = 'white'
  div.style.padding = '3px 7px'
  div.style.fontSize = '14px'
  div.style.border = '2px solid rgba(0,0,0,0.2)'
  div.style.borderRadius = '4px'

  const input = inputStyle(id, placeholder, width, min, max, type)
  const label = L.DomUtil.create('label')
  label.style.fontWeight = '500'
  label.style.margin = '0'

  label.innerHTML = `${labelName}：`
  div.appendChild(label)
  div.appendChild(input)
  L.DomEvent.disableClickPropagation(div)
  input.addEventListener('change', (e) => listener(e, input))
  return div
}

L.Marker.prototype.options.icon = DefaultIcon
L.control.message = opts => new L.Control.Message(opts)

const tooltipConf = { permanent: true, direction: 'bottom' }
const initDrawData = { longitude: undefined, latitude: undefined, radius: undefined }

function CircleMarker (props) {
  const { position, setPosition, locationInfo, setLocationInfo, editable, selectedLocation, setSelectedLocation, init, urlProps, editMode } = props
  const { searchKeyword, name, longitude, latitude, tags, radius, bounds } = locationInfo || {}
  const map = useMap()
  const { t: ln_t } = useTranslation()

  const [drawData, setDrawData] = useState({})
  const [drawing, setDrawing] = useState(false)
  const [save, setSave] = useState(false)
  const [tmpRadius, setTmpRadius] = useState(0)

  const drawRef = useRef(new L.FeatureGroup())
  const circleRef = useRef()
  const drawCircle = useRef()
  const markerRef = useRef()

  L.drawLocal = drawLocalSetting(ln_t)
  L.Control.Message = L.Control.extend({
    onAdd: function (map) {
      const div = L.DomUtil.create('div')
      div.style.fontWeight = '500'
      div.style.fontSize = '14px'
      div.style.color = 'rgb(189, 35, 35)'
      div.style.padding = '5px 15px'
      div.style.textAlign = 'center'
      div.style.borderRadius = '4px'
      div.style.border = '2px solid rgba(0,0,0,0.2)'
      div.style.backgroundColor = 'white'
      div.innerHTML = ln_t('message.error.coordinateNotFound')
      L.DomEvent.disableClickPropagation(div)
      return div
    }
  })
  const tootlip = (longitude, latitude, radius) => (`
    <div>${ln_t('location.longitude')}: ${longitude}</div> 
    <div>${ln_t('location.latitude')}: ${latitude}</div>
    <div>${ln_t('location.radius')}: ${Math.round(radius)} (${ln_t('location.meter')})</div>
  `)

  // ---------------- drawing circle on map ----------------
  const handleDrawing = ({ layer }) => {
    const radius = layer.getRadius()
    const { lng: longitude, lat: latitude } = layer.getLatLng()
    let max
    let min
    if (radius > 1000) {
      max = L.circle([latitude, longitude], 1000).bindTooltip(tootlip(longitude, latitude, 1000), tooltipConf)
      drawRef.current.removeLayer(layer)
      drawRef.current.addLayer(max)
      setDrawData({ longitude, latitude, radius: 1000 })
    } else if (radius < 25) {
      min = L.circle([latitude, longitude], 25).bindTooltip(tootlip(longitude, latitude, 25), tooltipConf)
      drawRef.current.removeLayer(layer)
      drawRef.current.addLayer(min)
      setDrawData({ longitude, latitude, radius: 25 })
    } else {
      drawCircle.current = layer
      layer.unbindTooltip()
      layer.bindTooltip(tootlip(longitude, latitude, radius), tooltipConf)
      setDrawData({ longitude, latitude, radius: Math.round(radius) })
    }
    drawCircle.current = radius > 1000 ? max : radius < 25 ? min : layer
  }

  useEffect(() => {
    if (!editable) return
    map.addLayer(drawRef.current)
    const option = {
      position: 'topleft',
      collapsed: true,
      draw: { polyline: false, polygon: false, rectangle: false, circle: true, marker: false, circlemarker: false },
      edit: { featureGroup: drawRef.current }
    }
    const drawControl = new L.Control.Draw(option)
    urlProps.type !== 'address' && map.addControl(drawControl)

    L.Control.RadiusInput = L.Control.extend({
      onAdd: map => {
        const listener = (e, input) => {
          const value = Number(e.target.value)
          const r = value > 1000 ? 1000 : value < 25 ? 25 : value
          setTmpRadius(r)
          input.value = r
        }
        return inputControl('半徑 (m)', 'radius', '請輸入半徑', '110px', 25, 1000, 'number', listener)
      }
    })
    L.control.radiusInput = opts => new L.Control.RadiusInput(opts)

    map.on('draw:drawstart', e => setDrawing(true))
      .on('draw:drawstop', e => setDrawing(false))
      .on('draw:created', e => {
        drawCircle.current && drawRef.current.removeLayer(drawCircle.current)
        setDrawData(initDrawData)
        const { layer } = e
        drawRef.current.addLayer(layer)
        handleDrawing(e)
        setSave(true)
        map.fitBounds(drawCircle.current.getBounds())
        setDrawing(false)
      }).on('draw:editresize', e => handleDrawing(e))
      .on('draw:editmove', e => handleDrawing(e))
      .on('draw:edited', () => { setSave(true); map.fitBounds(drawCircle.current.getBounds()) })
      .on('draw:deleted', e => {
        setDrawData(initDrawData)
        setSave(true)
        drawCircle.current = null
      })
  }, [map, editable])

  // ---------------- handle position and zoom ----------------
  useEffect(() => {
    if (bounds) map.fitBounds(bounds)
    if (position && radius && !editMode) map.fitBounds(circleRef.current.getBounds())
    else if (position && searchKeyword && !radius) map.setView(position, 17)
  }, [position, bounds])

  useEffect(() => {
    const hasGeo = latitude && longitude && radius
    const noTranslateResult = (!latitude || !longitude) && radius

    if (!editMode) return
    if (hasGeo) {
      drawCircle.current && drawRef.current.removeLayer(drawCircle.current)
      const draw = L.circle([latitude, longitude], radius).bindTooltip(tootlip(longitude, latitude, radius), tooltipConf)
      drawRef.current.addLayer(draw)
      drawCircle.current = draw
      setSelectedLocation({ ...selectedLocation, latitude, longitude, radius })
      map.fitBounds(draw.getBounds())
    }

    if (urlProps.type === 'address' && radius && init) {
      noTranslateResult && L.control.message({ position: 'topright' }).addTo(map)
      L.control.radiusInput({ position: 'topright' }).addTo(map)
      const radiusDom = document.getElementById('radius')
      if (radiusDom) radiusDom.value = radius
    }
  }, [locationInfo])

  useEffect(() => {
    if (tmpRadius) setLocationInfo({ ...locationInfo, radius: tmpRadius })
  }, [tmpRadius])

  useEffect(() => {
    if (!save) return
    setSelectedLocation({ ...selectedLocation, ...drawData })
    setSave(false)
  }, [save])

  const markerEvent = useMemo(() => ({
    dragend () {
      const marker = markerRef.current
      if (marker != null && searchKeyword) setPosition(marker.getLatLng())
    }
  }), [])

  return (
    (bounds || position) && searchKeyword
      ? <Marker position={position} ref={markerRef} draggable={!drawing} eventHandlers={markerEvent} />
      : editable
        ? ''
        : position && radius
          ? <Circle center={position} radius={radius} ref={circleRef}>
          <Tooltip permanent direction="top">
            <div>{ln_t('location.name')}: {name}</div>
            <div>{ln_t('location.coordinate')}: {latitude}, {longitude}</div>
            <div>{ln_t('location.radius')}: {radius} ({ln_t('location.meter')})</div>
            <div>{ln_t('location.label')}: {tags?.split('@').join(', ')}</div>
          </Tooltip>
        </Circle>
          : ''
  )
}

export default CircleMarker
